import React, { useState, useEffect } from 'react';
import { careersAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { MapPin, Clock, Briefcase, ArrowRight, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Careers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    careersAPI.listJobs()
      .then(res => setJobs(res.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="careers-page">
      {/* Hero */}
      <header className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 text-primary font-heading font-bold text-lg mb-6">
            <Briefcase className="h-5 w-5" /> TeamHub Karriere
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-5xl tracking-tight">Werde Teil unseres Teams</h1>
          <p className="text-muted-foreground mt-4 text-base max-w-2xl">
            Entdecke unsere offenen Stellen und bewirb dich in wenigen Minuten – ganz ohne Anmeldung.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        ) : jobs.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground">
            <Building2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
            Aktuell sind keine Stellen ausgeschrieben. Schau bald wieder vorbei!
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{jobs.length} offene Stelle{jobs.length === 1 ? '' : 'n'}</p>
            {jobs.map(job => (
              <Card key={job.id} className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/karriere/${job.id}${window.location.search}`)} data-testid={`careers-job-${job.id}`}>
                <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="min-w-0 space-y-2">
                    <h2 className="font-heading font-semibold text-lg">{job.title}</h2>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {job.location_name && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.location_name}</span>}
                      {job.employment_type && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {job.employment_type}</span>}
                      {job.department && <Badge variant="secondary" className="text-xs">{job.department}</Badge>}
                    </div>
                  </div>
                  <Button variant="outline" className="rounded-full shrink-0" data-testid={`careers-view-${job.id}`}>
                    Details <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-muted-foreground">Powered by TeamHub</div>
      </footer>
    </div>
  );
}
